/**
 * Canonical user_id derivation for cross-platform (web ↔ app) GA4 user stitching.
 *
 * MUST stay byte-for-byte identical to the Flutter app's `FirebaseBackend.hashEmail`
 * (Firebase project `vocalist-42ae3`): SHA-256 of the trimmed + lowercased email,
 * encoded as lowercase hex. GA4 only merges web and app sessions into one user when
 * both sides report the SAME `user_id`.
 *
 * Do NOT change the normalization (no Gmail-dot stripping, no uppercase hex, etc.)
 * without changing the app in lockstep — any divergence silently breaks the merge.
 *
 * Uses the native Web Crypto API, which requires a secure context (https or localhost).
 */

/** Trim + lowercase, nothing else. Mirrors the app exactly. */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** SHA-256 of `input`, returned as a 64-char lowercase hex string. */
export async function sha256Hex(input: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Canonical GA4 `user_id` for an email: SHA-256(trim+lowercase), lowercase hex. */
export function hashEmailToUserId(email: string): Promise<string> {
  return sha256Hex(normalizeEmail(email));
}
